import React, { useCallback, useEffect, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { FormInput } from 'components/molecules/FormInput';
import { FormSelect } from 'components/molecules/FormSelect';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { useConfigurationLicence } from 'pages/LicenceConfigurationPage/hooks';
import { useTranslations } from 'hooks/useTranslations';
import { Resolver, useForm } from 'react-hook-form';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { yupResolver } from '@hookform/resolvers/yup';
import Button from 'components/atoms/Button';
import { EIconTypes } from 'constants/Icons';
import { removeFileExtension, removeFileNameSpecialCharacters } from 'utils/files';
import { ActionTypes } from 'state/actions/alert';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { useAlert } from 'models/alertContext';
import { Box } from '@mui/system';
import { IDocument } from 'models/legalEntity';
import {
  ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE_IN_MB,
  validateAttachment,
} from '../../../utils/attachments';
import { useDocumentFormSchema } from './schema';
import { ImportFileContainer } from './styles';
import { colors } from 'theme/colors';
import FormLabel from 'components/atoms/FormLabel';
import { FormModal } from 'components/molecules/Modal';

const initialDocumentFormValues: IDocument = {
  name: '',
  weaponLicenceDocumentType: '',
  comment: '',
  file: null,
};

interface IDocumentForm {
  setToForm: (arg: any) => void;
  addFileModal: boolean;
  setAddFileModal: (arg: any) => void;
}

const DocumentForm = ({ setToForm, setAddFileModal, addFileModal }: IDocumentForm) => {
  const { t } = useTranslations();
  const [file, setfile] = useState<any>(null);
  const { attachmentDocumentTypeData } = useConfigurationLicence();
  const { schema } = useDocumentFormSchema();
  const { setAlert, clearAlert, samePageAlert } = useAlert();

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<IDocument>({
    defaultValues: initialDocumentFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<IDocument>,
  });

  useEffect(() => {
    if (file?.name) {
      setValue('name', removeFileNameSpecialCharacters(removeFileExtension(file.name)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const onSubmit = async (data: IDocument) => {
    const validation = validateAttachment(file, data.name);

    if (validation.hasError && validation.errorMessage) {
      await setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: t(validation.errorMessage),
        variant: EAlertVariants.error,
      });
      return;
    }

    if (file) {
      setToForm({ ...data, file });
      reset(initialDocumentFormValues);
      setfile(null);
      setAddFileModal(false);
    }
  };

  const handleFiles = (files: any) => {
    if (files.length) {
      setfile(files[0]);
      clearAlert(ActionTypes.SET_SAME_PAGE_ALERT);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  };

  return (
    <FormModal
      open={addFileModal}
      onClickClose={() => {
        reset(initialDocumentFormValues);
        setAddFileModal(false);
      }}
      onClickApprove={handleSubmit(onSubmit)}
      title={t('legalEntity.licence.addAttachment.label')}
      titleLeft
      minWidth="90%"
    >
      <Grid container size={{ xs: 12 }} columnSpacing={2} sx={{ rowGap: 1 }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <FormLabel name="attachment" label={t('legalEntity.licence.attachment.label')} />
          <ImportFileContainer
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => document.getElementById('fileInput')?.click()}
            style={{ cursor: 'pointer' }}
          >
            <Grid
              sx={{
                display: 'flex',
                gap: '4px',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
              }}
            >
              <Typography variant="body2" sx={{ color: colors.primary600 }}>
                {t('addAttachment.addFile.label')}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.gray400 }}>
                {t('addAttachment.dragDrop.label')}
              </Typography>
            </Grid>

            <Grid
              sx={{
                display: 'flex',
                gap: '4px',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
              }}
            >
              <Typography variant="caption" sx={{ color: colors.gray400 }}>
                {t('addAttachment.allowedFileFormats.label')}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.gray400 }}>
                {t('addAttachment.maxSizeMB.label').replace(
                  '{0}',
                  MAX_FILE_SIZE_IN_MB.toString(),
                )}
              </Typography>
            </Grid>
            <input
              id="fileInput"
              type="file"
              accept={ALLOWED_FILE_TYPES.join(', ')}
              hidden
              onChange={handleFileInputChange}
            />
          </ImportFileContainer>
        </Grid>
        <Grid
          container
          size={{ xs: 12, md: 4 }}
          sx={{ alignContent: 'flex-start', rowGap: 1 }}
        >
          <Grid size={{ xs: 12 }}>
            <FormInput
              name="name"
              label={t('legalEntity.licence.fileName.label')}
              setValue={setValue}
              control={control}
              required
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormSelect
              options={mapDictionaryToOptionProp(attachmentDocumentTypeData)}
              setValue={setValue}
              name="weaponLicenceDocumentType"
              label={t('legalEntity.licence.documentType.label')}
              control={control}
              required
              errors={errors}
            />
          </Grid>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <FormInput
            name="comment"
            label={t('legalEntity.licence.comment.label')}
            setValue={setValue}
            control={control}
            errors={errors}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          {samePageAlert && (
            <Box sx={{ mb: 2 }}>
              <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
            </Box>
          )}
        </Grid>
      </Grid>
    </FormModal>
  );
};

export { DocumentForm };
